<template>
  <div>
    <section class="pt-125 md:pt-175 lg:pt-200 mb-100">
      <div class="container">
        <div class="max-w-screen-xl mx-auto">
          <Breadcrumb :breadcrumbs="[data?.index, data?.overview, data?.entry].filter((b) => b)" />

          <div class="grid md:grid-cols-2 mt-100">
            <h1
              v-if="data?.entry?.heading"
              class="wysiwyg-title text-4xl md:text-5xl lg:text-6xl xl:text-7xl md:-ml-150 md:translate-x-150 text-primary-500"
              v-html="data.entry.heading"
            />
            <div class="md:row-span-3">
              <Media
                v-if="data?.entry?.image?.length"
                :media="data.entry.image[0]"
                sizes="(max-width: 600px) 345px, (max-width: 1024px) 497px, 100vw"
                :alt="data.entry.title"
                width="1280"
                height="1330"
                class="mt-[-63px] md:mt-100 w-full"
                loading="lazy"
              />
            </div>
            <div
              v-if="data?.entry?.intro"
              class="mt-25 md:pr-50 lg:pr-100 text-lg wysiwyg"
              v-html="data.entry.intro"
            />
          </div>
        </div>
      </div>
    </section>

    <ContentBlocks :content-blocks="data?.entry?.contentBlocks" />

    <div class="container">
      <div class="max-w-screen-xl mx-auto">
        <div class="w-full md:w-2/3 mt-50">
          <p
            v-if="data?.entry?.blogAuthorName"
            class="text-secondary py-10 border-t border-b border-primary-200"
          >
            {{ $t('blog.author') }}
            <img
              v-if="data.entry.blogAuthorAvatar?.length"
              :src="data.entry.blogAuthorAvatar[0]?.url"
              class="inline-block w-40 h-40 rounded-full mx-10"
              :alt="data.entry.blogAuthorName"
            >
            <span class="font-medium">{{ data.entry.blogAuthorName }}</span>
            {{ $t('general.on') }} {{ formatDateTime(data.entry.postDate, { dateStyle: 'long' }) }}
          </p>
        </div>
      </div>
    </div>

    <section class="my-100">
      <div class="container">
        <div class="pr-60">
          <h2
            class="wysiwyg-title text-3xl md:text-4xl mr-20 text-secondary"
            v-html="$t('blog.more')"
          />
          <NuxtLink
            :to="`/${data?.overview?.uri}`"
            class="inline-flex items-center text-xl font-serif underline space-x-5 group"
          >
            <span>{{ $t('blog.view-all') }}</span>
            <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
          </NuxtLink>
        </div>

        <Swiper
          v-if="data?.interiorViewersEntries?.length"
          slides-per-view="auto"
          :navigation="true"
          :modules="[SwiperNavigation]"
          class="mt-30"
        >
          <SwiperSlide
            v-for="post in data.interiorViewersEntries"
            :key="`related-post-${post.id}`"
            class="mr-25 last:mr-0"
          >
            <NuxtLink
              :to="`/${post.uri}`"
              class="group"
            >
              <Media
                v-if="post.image?.length"
                :media="post.image[0]"
                :alt="post.title"
                loading="lazy"
              />
              <h4 class="text-xl font-bold group-hover:underline mt-20">
                {{ post.title }}
              </h4>
              <p
                v-if="post.intro"
                class="mt-10"
                v-html="post.intro"
              />
            </NuxtLink>
          </SwiperSlide>
        </Swiper>
      </div>
    </section>
  </div>
</template>

<script setup>
import {
  useI18n,
  useAsyncGql,
  useSeo,
  watch,
  useRoute,
  formatDateTime,
  SwiperNavigation,
} from '#imports';

// General icons
import chevronIcon from '@/assets/img/icon-chevron.svg';

// Data
const { locale } = useI18n({ useScope: 'global' });
const route = useRoute();
const { data } = await useAsyncGql(route.meta?.handle, {
  site: locale.value,
  uri: route.path.substring(1).replace(/\/+$/, ''),
});

// Watcher
watch(data, (value) => {
  if (value?.entry?.seomatic) {
    useSeo(value.entry.seomatic);
  }
}, { immediate: true });
</script>
